import React from 'react'
import {
  Link,
  NavigateFunction,
  useNavigate,
  useParams,
} from 'react-router-dom'
import { Paper, Stack } from '@mui/material'
import { AboveFoldLayout, Button } from 'flanders-common-ui'
import { organizations } from 'routes/routes'
import { GridColDef } from '@mui/x-data-grid'
import { Site, useFetchSitesListPaginated } from 'api/sites'
import Status from 'components/molecules/Status'
import DataGrid from 'components/molecules/DataGrid'
import { ModeOutlined } from '@mui/icons-material'
import { formatDate } from 'utils/date-utils'

function useColumns({
  navigate,
  organizationId,
}: {
  navigate: NavigateFunction
  organizationId: string
}): GridColDef<Site>[] {
  const columns: GridColDef<Site>[] = [
    {
      field: 'name',
      headerName: 'Site Name',
      flex: 2,
      renderCell: ({ row: site }) => {
        return (
          <Link key={site.id} to={`/sites/${site.id}`} state={{ site }}>
            {site.name}
          </Link>
        )
      },
    },
    {
      field: 'address',
      headerName: 'City/State',
      flex: 1,
      valueGetter: ({ row: site }) => `${site.city}, ${site.state}`,
    },
    {
      field: 'locationCount',
      headerName: 'Locations',
      flex: 1,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: ({ row: siteData }) => {
        if (siteData.status === 'requires_attention') {
          return (
            <Status
              status={`${siteData.requiresAttentionCount} Location(s)`}
              description="Require Attention"
            />
          )
        } else if (siteData.status === 'gateway_offline') {
          return (
            <Status
              status="Gateway Offline"
              description={`Last Heartbeat: ${formatDate(siteData.lastHeartbeatAt)}`}
            />
          )
        } else {
          return <Status status="Healthy" showCheckIcon={true} />
        }
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      headerAlign: 'right',
      flex: 1,
      align: 'right',
      renderCell: ({ row: site }) => {
        const commonIconProps = { width: 24, height: 24, cursor: 'pointer' }

        return (
          <ModeOutlined
            color="disabled"
            sx={{ ...commonIconProps }}
            onClick={() => {
              navigate(
                `${organizations}/${organizationId}/sites/${site.id}/edit`
              )
            }}
          />
        )
      },
    },
  ]

  return columns
}

const OrganizationSitesPage: React.FC = () => {
  const { organizationId = '' } = useParams<{ organizationId: string }>()
  const navigate = useNavigate()

  const columns = useColumns({
    navigate: navigate,
    organizationId: organizationId,
  })

  return (
    <>
      <AboveFoldLayout breadcrumbRoutes={[]} heading="Organization Sites">
        <Button
          size="small"
          onClick={() => {
            navigate(`${organizations}/${organizationId}/sites/add`)
          }}
        >
          + New Site
        </Button>
      </AboveFoldLayout>
      <Stack component={Paper} spacing={1} px={2} pt="20px">
        <DataGrid<Site, { organizationId: string }>
          columns={columns}
          dataId="sites-table"
          requestExtraParams={{
            organizationId,
          }}
          useFetchPaginatedData={useFetchSitesListPaginated}
        />
      </Stack>
    </>
  )
}

export default OrganizationSitesPage
